import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  RefreshCw,
  Trash2,
  Eye,
  EyeOff,
  Pencil,
  FileText,
  ExternalLink,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { useAdminList, API, authHeaders } from "./adminApi";
import ImagePicker from "./ImagePicker";

const EMPTY = {
  slug: "",
  title: "",
  excerpt: "",
  category: "Conseils",
  image: "",
  body: "",
  keywords: [],
  read_min: 5,
  published: true,
};

const slugify = (s) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);

export default function BlogAdmin({ token, onUnauthorized }) {
  const { data, loading, refresh, setData } = useAdminList(
    token,
    "/admin/blog/posts",
    onUnauthorized,
  );
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(EMPTY);

  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const save = async () => {
    const payload = {
      ...draft,
      keywords: typeof draft.keywords === "string"
        ? draft.keywords.split(",").map((k) => k.trim()).filter(Boolean)
        : draft.keywords,
    };
    try {
      if (creating) {
        const res = await axios.post(`${API}/admin/blog/posts`, payload, {
          headers: authHeaders(token),
        });
        setData((d) => [res.data, ...d]);
        toast.success("Article publié");
      } else if (editing) {
        const res = await axios.put(
          `${API}/admin/blog/posts/${editing.id}`,
          payload,
          { headers: authHeaders(token) },
        );
        setData((d) => d.map((x) => (x.id === editing.id ? res.data : x)));
        toast.success("Article modifié");
      }
      close();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer cet article ? Cette action est irréversible.")) return;
    await axios.delete(`${API}/admin/blog/posts/${id}`, { headers: authHeaders(token) });
    setData((d) => d.filter((x) => x.id !== id));
    toast.success("Article supprimé");
  };

  const togglePublished = async (post) => {
    const res = await axios.put(
      `${API}/admin/blog/posts/${post.id}`,
      {
        slug: post.slug,
        title: post.title,
        excerpt: post.excerpt,
        category: post.category,
        image: post.image,
        body: post.body,
        keywords: post.keywords,
        read_min: post.read_min,
        published: !post.published,
      },
      { headers: authHeaders(token) },
    );
    setData((d) => d.map((x) => (x.id === post.id ? res.data : x)));
  };

  return (
    <div data-testid="admin-blog-page" className="space-y-6">
      <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-semibold text-[#1A2E1C]">
            Articles de blog
          </h2>
          <p className="text-[#5C705E] mt-1">
            Publiez des conseils pour booster votre référencement local.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={refresh}
            variant="outline"
            className="rounded-full border-[#E5EBE5] text-[#1B5E20] hover:bg-[#E8F5E9]"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Actualiser
          </Button>
          <Button
            onClick={() => {
              setDraft(EMPTY);
              setCreating(true);
            }}
            data-testid="blog-create"
            className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Nouvel article
          </Button>
        </div>
      </header>

      {(() => {
        if (loading) {
          return <div className="text-center py-20 text-[#5C705E]">Chargement…</div>;
        }
        if (data.length === 0) {
          return (
            <div className="text-center py-20 border-2 border-dashed border-[#E5EBE5] rounded-3xl">
              <FileText className="w-16 h-16 text-[#A5D6A7] mx-auto mb-4" />
              <p className="text-[#5C705E]">
                Aucun article publié depuis l'admin. Les 3 articles initiaux sont gérés dans le code.
              </p>
            </div>
          );
        }
        return (
          <div className="grid gap-3" data-testid="blog-list">
            {data.map((post) => (
            <div
              key={post.id}
              data-testid={`blog-${post.id}`}
              className={`bg-white border rounded-2xl p-4 flex flex-col md:flex-row gap-4 hover:shadow-md transition ${
                post.published ? "border-[#E5EBE5]" : "border-[#FFCC80] bg-[#FFF8E1]"
              }`}
            >
              {post.image ? (
                <img
                  src={post.image}
                  alt={post.title}
                  className="w-full md:w-32 h-32 md:h-20 rounded-xl object-cover border border-[#E5EBE5]"
                  loading="lazy"
                />
              ) : (
                <div className="w-full md:w-32 h-32 md:h-20 rounded-xl bg-[#E8F5E9] flex items-center justify-center">
                  <FileText className="w-6 h-6 text-[#1B5E20]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <span className="text-xs uppercase tracking-wider text-[#1B5E20] font-semibold">
                    {post.category}
                  </span>
                  {!post.published && (
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#FFCC80] text-[#7B5300] font-semibold uppercase tracking-wider">
                      Brouillon
                    </span>
                  )}
                </div>
                <h3 className="font-['Outfit'] font-semibold text-[#1A2E1C] truncate">
                  {post.title}
                </h3>
                <p className="text-sm text-[#5C705E] line-clamp-2">{post.excerpt}</p>
                <div className="text-xs text-[#5C705E]/70 mt-2">
                  /conseils/{post.slug} · {post.read_min} min
                </div>
              </div>
              <div className="flex items-center gap-2 self-end md:self-center">
                <a
                  href={`/conseils/${post.slug}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 rounded-full hover:bg-[#E8F5E9]"
                  title="Voir l'article"
                >
                  <ExternalLink className="w-4 h-4 text-[#1B5E20]" />
                </a>
                <button onClick={() => togglePublished(post)} className="p-2 rounded-full hover:bg-[#E8F5E9]">
                  {post.published ? (
                    <Eye className="w-4 h-4 text-[#1B5E20]" />
                  ) : (
                    <EyeOff className="w-4 h-4 text-[#7B5300]" />
                  )}
                </button>
                <button
                  onClick={() => {
                    setDraft({
                      ...EMPTY,
                      ...post,
                      keywords: (post.keywords || []).join(", "),
                    });
                    setEditing(post);
                  }}
                  data-testid={`blog-edit-${post.id}`}
                  className="p-2 rounded-full hover:bg-[#E8F5E9]"
                >
                  <Pencil className="w-4 h-4 text-[#1B5E20]" />
                </button>
                <button onClick={() => remove(post.id)} className="p-2 rounded-full hover:bg-red-50">
                  <Trash2 className="w-4 h-4 text-[#D32F2F]/80" />
                </button>
              </div>
            </div>
          ))}
        </div>
        );
      })()}

      <Dialog open={creating || !!editing} onOpenChange={(o) => !o && close()}>
        <DialogContent
          aria-describedby="blog-form-desc"
          className="max-w-3xl max-h-[90vh] overflow-y-auto rounded-3xl p-6 border-[#E5EBE5]"
        >
          <DialogHeader>
            <DialogTitle className="font-['Outfit'] text-2xl font-semibold text-[#1A2E1C]">
              {creating ? "Nouvel article" : "Modifier l'article"}
            </DialogTitle>
            <p id="blog-form-desc" className="text-sm text-[#5C705E]">
              Le contenu accepte le format <strong>Markdown</strong> (titres ##, gras **, listes -, liens [texte](url)).
            </p>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block">Titre *</Label>
                <Input
                  value={draft.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setDraft((d) => ({
                      ...d,
                      title,
                      slug: creating ? slugify(title) : d.slug,
                    }));
                  }}
                  className="bg-white border-[#E5EBE5] rounded-xl h-11"
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Slug URL *</Label>
                <Input
                  value={draft.slug}
                  onChange={(e) => setDraft({ ...draft, slug: slugify(e.target.value) })}
                  className="bg-white border-[#E5EBE5] rounded-xl h-11"
                />
              </div>
            </div>
            <div>
              <Label className="mb-1.5 block">Résumé court (extrait)</Label>
              <Textarea
                value={draft.excerpt}
                onChange={(e) => setDraft({ ...draft, excerpt: e.target.value })}
                rows={2}
                placeholder="2-3 phrases qui donnent envie de cliquer."
                className="bg-white border-[#E5EBE5] rounded-xl"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="mb-1.5 block">Catégorie</Label>
                <Input
                  value={draft.category}
                  onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                  className="bg-white border-[#E5EBE5] rounded-xl h-11"
                />
              </div>
              <div>
                <Label className="mb-1.5 block">Durée lecture (min)</Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={draft.read_min}
                  onChange={(e) => setDraft({ ...draft, read_min: Number(e.target.value) || 1 })}
                  className="bg-white border-[#E5EBE5] rounded-xl h-11"
                />
              </div>
            </div>
            <ImagePicker
              token={token}
              label="Image de couverture"
              value={draft.image}
              onChange={(url) => setDraft({ ...draft, image: url })}
            />
            <div>
              <Label className="mb-1.5 block">Mots-clés SEO (séparés par virgule)</Label>
              <Input
                value={draft.keywords}
                onChange={(e) => setDraft({ ...draft, keywords: e.target.value })}
                placeholder="paysagiste creuse, entretien jardin, taille de haie"
                className="bg-white border-[#E5EBE5] rounded-xl h-11"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Corps de l'article (Markdown)</Label>
              <Textarea
                value={draft.body}
                onChange={(e) => setDraft({ ...draft, body: e.target.value })}
                rows={14}
                placeholder="## Mon titre H2&#10;&#10;Un paragraphe avec **du gras** et un [lien](https://...).&#10;&#10;- Point 1&#10;- Point 2"
                className="bg-white border-[#E5EBE5] rounded-xl font-mono text-sm"
              />
            </div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.published}
                onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                className="w-5 h-5"
              />
              <span className="text-sm">Publié (visible sur le blog)</span>
            </label>
          </div>
          <div className="mt-6 flex gap-3 justify-end sticky bottom-0 bg-white pt-3 border-t border-[#E5EBE5]">
            <Button variant="outline" onClick={close} className="rounded-full">
              Annuler
            </Button>
            <Button
              onClick={save}
              disabled={!draft.title || !draft.slug || !draft.excerpt}
              className="rounded-full bg-[#1B5E20] hover:bg-[#164d1a] text-white"
            >
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
